import type { RequestOptions } from '@/libs/api/types';
import {
  invitePlayerGuardian,
  type InvitePlayerGuardianRequest,
} from '@/services/invite-player-guardian';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { playerGuardiansKeys } from './use-player-guardians-query';
import { invitationPreviewKeys } from './use-player-invitations-query';

/**
 * Invites a co-guardian for a player via
 * POST `/api/v1/guardians/me/players/{playerId}/invitations`, then invalidates that
 * player's circle so the new invitation shows up on refetch.
 *
 * Pass `options` (e.g. `{ skipServerErrorAlert: true }`) when the call site shows its
 * own tailored error UI instead of the shared server-error popup. Omitting it keeps the
 * default behaviour for existing callers (Onboarding, Resend).
 */
export function useInvitePlayerGuardianMutation(playerId: string, options?: RequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: InvitePlayerGuardianRequest) =>
      invitePlayerGuardian(playerId, body, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: playerGuardiansKeys.list(playerId) });
      // Resend re-issues the invite → refresh the invitation detail (preview by token)
      // so its status/expiry update in place on the Invitation Sent screen.
      queryClient.invalidateQueries({ queryKey: invitationPreviewKeys.all });
    },
  });
}
